/**
 * Les courses visées et leur plan (`docs/plan-course.md`).
 *
 * Le plan, les conflits et leurs phrases viennent du serveur, calculés par règles :
 * l'écran ne compte aucune semaine et ne décide d'aucune priorité.
 */

import { request } from '@/lib/api';

export type Distance =
  '5k' | '10k' | 'semi' | 'marathon' | 'sprint' | 'olympique' | '70.3' | 'ironman';
export type Priority = 'A' | 'B' | 'C';
export type Phase =
  'base' | 'développement' | 'spécifique' | 'récupération' | 'affûtage' | 'course';

export interface Race {
  id: number;
  token: string;
  race_id: string;
  name: string;
  date: string;
  distance: Distance;
  distance_label: string;
  priority: Priority;
  target: string | null;
  note: string | null;
  countdown: string;
  past: boolean;
}

export interface Conflict {
  key: string;
  keep: string;
  demote: string;
  text: string;
}

/**
 * Le plan posé ne suit plus sa course : sa date a changé (M-17), elle n'est plus en A, ou
 * elle a été retirée (session 10 b). Le serveur le dit, rédige la phrase — ce qui partirait
 * compris — et dit quels gestes s'offrent : l'écran ne décide d'aucun.
 */
export interface StalePlan {
  reason: 'date' | 'priority' | 'removed';
  /** La course qui porte le plan ; `null` quand elle a été retirée. */
  race_id: string | null;
  title: string;
  text: string;
  /** La course pour laquelle « Reposer le plan » repose ; `null` : rien à reposer. */
  repose: string | null;
  /** Le retrait est-il offert ici. */
  clear: boolean;
  /** Les séances qui partiraient. */
  leaving: number;
}

/** Une distance du formulaire, servie depuis `races/catalog.SPECS` : libellé compris. */
export interface DistanceOption {
  value: Distance;
  label: string;
}

export interface RaceList {
  today: string;
  races: Race[];
  distances: DistanceOption[];
  conflicts: Conflict[];
  next_a: string | null;
  stale_plan: StalePlan | null;
}

/** La prochaine course A, telle que « Où je vais » la dit (M-19). Servie par le tableau
 *  de bord : le décompte, l'état du plan posé et sa phrase arrivent rédigés. */
export interface RaceAim {
  race: Race;
  /** « J-12 », « Jour J ». */
  countdown: string;
  plan: 'absent' | 'posed' | 'stale';
  phase: Phase | null;
  /** « Semaine 3 sur 16 — développement ». */
  phase_text: string | null;
  stale_plan: StalePlan | null;
}

export interface RacePayload {
  name: string;
  date: string;
  distance: Distance;
  priority: Priority;
  target?: string | null;
  note?: string | null;
}

export interface PlanSession {
  date: string;
  weekday: string;
  title: string;
  notation: string;
  minutes: number;
  km: number | null;
  race: boolean;
}

export interface PlanWeek {
  start: string;
  phase: Phase;
  km: number;
  long_km: number;
  sessions: PlanSession[];
}

export interface PlanView {
  race_id: string;
  summary: string;
  warnings: string[];
  weeks: PlanWeek[];
  placed: number;
}

export interface Placed {
  created: number;
  removed: number;
}

export const racesApi = {
  list: () => request<RaceList>('/api/races'),
  create: (payload: RacePayload) =>
    request<RaceList>('/api/races', { method: 'POST', body: payload }),
  /** Corriger une course visée, sous `If-Match` (M-17). */
  update: (id: number, token: string, payload: RacePayload) =>
    request<RaceList>(`/api/races/${String(id)}`, {
      method: 'PATCH',
      body: payload,
      headers: { 'If-Match': token },
    }),
  remove: (id: number, token: string) =>
    request<RaceList>(`/api/races/${String(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': token },
    }),
  decide: (key: string, accept: boolean) =>
    request<RaceList>(`/api/races/conflicts/${encodeURIComponent(key)}`, {
      method: 'POST',
      body: { accept },
    }),
  plan: (raceId: string) => request<PlanView>(`/api/races/plan/${raceId}`),
  place: (raceId: string) => request<Placed>(`/api/races/plan/${raceId}`, { method: 'POST' }),
  clear: () => request<Placed>('/api/races/plan', { method: 'DELETE' }),
};
